import { useCallback, useMemo } from 'react';
import { PREVIEW_DATA_SOURCE_QUERY_KEY } from '../../../../const';
import DataSourceHelper from '../../../../helper/DataSourceHelper';
import type { UnsavedDataSource } from '../../../../stores/DataSourceRecordStore';
import { InterfaceModes } from '../../../../stores/InterfaceStore';
import {
  useDSCActions,
  useDSCDataSourceRecordStore,
  useDSCGetState,
  useDSCMode,
  useDSCPageMetadata,
} from '../../context/selectors';
import type { QueryNode } from '../../types';
import { getLookupRequestsForDataSources } from '../../utils/getLookupRequestsForDataSources';
import ApplicationDataSourceQueryNode from '../ApplicationDataSourceQueryNode';
import usePreviewDataSourceIds from './usePreviewDataSourceIds';

function usePreviewMergedDataSources() {
  const storeDataSources = useDSCDataSourceRecordStore().hooks.useDataSources();
  const unsavedDataSources = useDSCDataSourceRecordStore().hooks.useUnsavedDataSources();

  const adaptedDataSources = useMemo(() => {
    const allDataSourcesKeys = Object.keys(storeDataSources);
    return allDataSourcesKeys.reduce<Record<string, UnsavedDataSource | undefined>>((acc, id) => {
      acc[id] = unsavedDataSources[id] || storeDataSources[id];
      return acc;
    }, {});
  }, [storeDataSources, unsavedDataSources]);

  return { dataSources: adaptedDataSources };
}

function PreviewDataSourceController() {
  const { dataSources } = usePreviewMergedDataSources();
  const { dependencyById: lookupDependencyById, dependencies: lookupDependencies } =
    useDSCPageMetadata() ?? {};
  const getDataSourceControllerState = useDSCGetState();
  const interfaceMode = useDSCMode();
  const { ids } = usePreviewDataSourceIds();
  const { setPreviewDataInDSMeta, previewDataSource } = useDSCActions();

  const onDataSourceCompletion: QueryNode['onQueryCompletion'] = useCallback(
    ({ id }) => {
      previewDataSource(id, false);
    },
    [previewDataSource],
  );

  const lookupRequestsByDataSourceId = useMemo(
    () => getLookupRequestsForDataSources(lookupDependencies, lookupDependencyById),
    [lookupDependencies, lookupDependencyById],
  );

  const getCurrentQueryResult: QueryNode['getCurrentQueryResult'] = useCallback(
    ({ id }) => {
      return getDataSourceControllerState().dataSourcesMeta[id]?.previewResult;
    },
    [getDataSourceControllerState],
  );

  const onSetDataSourceState: QueryNode['setDataSourceState'] = useCallback(
    ({ id, result }) => {
      setPreviewDataInDSMeta(id, result);
    },
    [setPreviewDataInDSMeta],
  );

  if (interfaceMode !== InterfaceModes.BUILDER) return null;

  return (
    <>
      {ids.map((dsId) => {
        const dataSource = dataSources[dsId]?.properties;
        const lookupRequests = lookupRequestsByDataSourceId[dsId];

        if (!dataSource) return null;

        const dependsOn = DataSourceHelper.getDependsOnFromProperties(dataSource);
        const dynamicPaths = DataSourceHelper.getDynamicPathsFromProperties(dataSource);

        if (dataSource.type === 'APPLICATION') {
          return (
            <ApplicationDataSourceQueryNode
              dataSource={dataSource}
              dependsOn={dependsOn}
              dynamicPaths={dynamicPaths}
              getCurrentQueryResult={getCurrentQueryResult}
              id={dsId}
              key={`${PREVIEW_DATA_SOURCE_QUERY_KEY}${dsId}`}
              lookupRequests={lookupRequests}
              onQueryCompletion={onDataSourceCompletion}
              originalDataSourceId={dataSource.standardDataSourceId ?? dsId}
              refetchOnPayloadChange={false}
              requestId={`${PREVIEW_DATA_SOURCE_QUERY_KEY}${dsId}`}
              setDataSourceState={onSetDataSourceState}
            />
          );
        }
        return null;
      })}
    </>
  );
}

export default PreviewDataSourceController;
